import { Add, KeyboardArrowLeft } from "@mui/icons-material";
import { Button } from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, { useContext, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import DotElastic from "components/DotElastic";
import AddPaymentMethod, {
  AddPaymentMethodRef,
} from "components/Payment/UpgradeFlowComponents/AddPaymentMethod";
import Card from "components/Payment/UpgradeFlowComponents/Card";
import { SLAPSHOT_MY_PLAN_URL } from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Subscription } from "models/User";
import { setDefaultPaymentMethod } from "services/SubscriptionService";

import styles from "./ManagePaymentMethodsModal.module.scss";

interface ManagePaymentMethodsModalProps {
  onBack: () => void;
  paymentMethods: PaymentMethod[] | null;
  defaultMethod: PaymentMethod | null;
  fetchPaymentMethods: () => void;
  subscription: Subscription;
  embedded?: boolean;
}

const ManagePaymentMethodsModal: React.FC<ManagePaymentMethodsModalProps> = ({
  onBack,
  paymentMethods,
  defaultMethod,
  fetchPaymentMethods,
  subscription,
  embedded,
}) => {
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const addPaymentMethodRef = useRef<AddPaymentMethodRef>(null);

  const location = useLocation();

  return (
    <div
      className={styles.container}
      style={
        embedded
          ? {
              background: "#13131a",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }
          : undefined
      }
    >
      <div className={styles.content}>
        <div className={styles.back} onClick={onBack}>
          <KeyboardArrowLeft className={styles.arrowLeft} />
          <span>Back to Billing</span>
        </div>

        {!showAddPaymentModal && (
          <>
            <div className={styles.heading}>Manage payment methods</div>

            {paymentMethods == null && (
              <div className={styles.loader}>
                <DotElastic />
              </div>
            )}

            {paymentMethods && (
              <div className={styles.cards}>
                {paymentMethods.map((paymentMethod) => {
                  return (
                    <Card
                      key={paymentMethod.id}
                      isDefault={paymentMethod.id === defaultMethod?.id}
                      paymentMethod={paymentMethod}
                      onRemove={() => {
                        fetchPaymentMethods();
                      }}
                      onClick={() => {}}
                      setDefaultMethod={() => {
                        fetchPaymentMethods();
                      }}
                      hideOptions={
                        !!subscription &&
                        subscription.plan.price.unit_amount_decimal > 0 &&
                        paymentMethod.id === defaultMethod?.id
                      }
                      subscription={subscription}
                    />
                  );
                })}
              </div>
            )}

            <Button
              className={cx(styles.slapShotButton, styles.addCard)}
              onClick={() => {
                setShowAddPaymentModal(true);
              }}
            >
              <Add fontSize="small" /> Add new card
            </Button>
          </>
        )}

        {showAddPaymentModal && (
          <div className={styles.addPaymentMethod}>
            <div className={styles.heading}>Enter card details</div>

            <AddPaymentMethod
              ref={addPaymentMethodRef}
              onSuccess={(setupIntent) => {
                if (!defaultMethod) {
                  setDefaultPaymentMethod(
                    subscription?.id,
                    setupIntent.payment_method,
                  ).then((data) => {});
                }
                fetchPaymentMethods();
                setShowAddPaymentModal(false);
              }}
              returnUrl={`${location.pathname}${location.search}`}
              subscription={subscription}
            />

            <div className={styles.actions}>
              <Button
                className={cx(styles.noBackgroundButton)}
                onClick={() => {
                  setShowAddPaymentModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                className={styles.slapShotButton}
                onClick={() => {
                  addPaymentMethodRef.current?.confirm();
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagePaymentMethodsModal;
